import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Info, UserMinus, UserPlus, Wand2, X } from 'lucide-react';
import { buildDynamicRule } from '@/api/dynamicRules';
import { listDepartments } from '@/api/departments';
import { listPositionTitles } from '@/api/positionTitles';
import { listPeople } from '@/api/people';
import { cn } from '@/lib/cn';
import { CopyButton } from '@/pages/OnPass/CopyButton';
import { TITLES_KEY } from '@/pages/Onboarding/useRehomeTitle';
import type { DynamicRuleBuildRequest, Person, RuleAccountState, RuleMember } from '@/types/oscar';
import { addUnique } from './builderCatalogue';
import { DepartmentPicker } from './DepartmentPicker';
import { describeSearchError } from './describeSearchError';

/**
 * Dynamic rule builder — IT Support picks department codes, position titles and
 * named people, presses Create rule, and copies an Entra dynamic membership rule
 * to paste into a group themselves. Nothing is written to Entra.
 *
 * Departments match Entra's user.department, which holds the AD department
 * code; titles picked one by one match user.jobTitle. The preview is Oscar's
 * people register (synced from Entra), and "Not caught" lists people Oscar puts
 * in a picked department (from their job title) whose Entra department is blank
 * or different, so the team can fix Entra or name them before pasting.
 *
 * Server: POST /api/dynamic-rules/build (admin + helpdesk).
 */

const STATES: { value: RuleAccountState; label: string; hint: string }[] = [
  { value: 'Enabled', label: 'Enabled accounts only', hint: 'Adds user.accountEnabled -eq true' },
  { value: 'Disabled', label: 'Disabled accounts only', hint: 'Adds user.accountEnabled -eq false' },
  { value: 'Any', label: 'Enabled and disabled', hint: 'No account condition' },
];

const schema = z.object({
  DepartmentCodes: z.array(z.string()),
  PositionTitles: z.array(z.string()),
  IncludeObjectIds: z.array(z.string()),
  ExcludeObjectIds: z.array(z.string()),
  AccountState: z.enum(['Enabled', 'Disabled', 'Any']),
}).refine((v) => v.DepartmentCodes.length + v.PositionTitles.length + v.IncludeObjectIds.length > 0, {
  message: 'Add at least one department, position title or person to include.',
  path: ['DepartmentCodes'],
});

const EMPTY: DynamicRuleBuildRequest = {
  DepartmentCodes: [], PositionTitles: [], IncludeObjectIds: [], ExcludeObjectIds: [], AccountState: 'Enabled',
};

interface Named { name: string; email: string }

const REASON: Record<RuleMember['Reason'], string> = {
  Department: 'Department',
  Title: 'Job title',
  Named: 'Named in the rule',
  DepartmentMismatch: '',
};

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
/** Has anything been picked since the shown rule was made? */
const sameRequest = (a: DynamicRuleBuildRequest, b: DynamicRuleBuildRequest) =>
  a.AccountState === b.AccountState
  && sameList(a.DepartmentCodes, b.DepartmentCodes) && sameList(a.PositionTitles, b.PositionTitles)
  && sameList(a.IncludeObjectIds, b.IncludeObjectIds) && sameList(a.ExcludeObjectIds, b.ExcludeObjectIds);

const fmt = (n: number) => n.toLocaleString('en-AU');
const MIN_SEARCH = 2;

function Chip({ children, onRemove, label }: { children: ReactNode; onRemove: () => void; label: string }) {
  return (
    <li className="inline-flex max-w-full items-center gap-1 rounded-full border border-navy/30 bg-navy/5 py-0.5 pl-3 pr-1 text-sm text-navy">
      <span className="truncate">{children}</span>
      <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="rounded-full p-0.5 hover:bg-navy/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy">
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </li>
  );
}

function StepHeading({ n, children }: { n: number; children: ReactNode }) {
  return (
    <h2 className="mb-2 flex items-center gap-2 font-heading text-base font-bold text-navy">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-navy text-xs text-white">{n}</span>
      {children}
    </h2>
  );
}

function MemberTable({ rows, caption, action }: {
  rows: RuleMember[];
  caption: string;
  action?: (m: RuleMember) => ReactNode;
}) {
  return (
    <div className="max-h-96 overflow-auto rounded border border-border">
      <table className="w-full min-w-[40rem] text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="sticky top-0 bg-navy text-xs uppercase tracking-wide text-white">
          <tr>
            <th scope="col" className="px-3 py-2">Name</th>
            <th scope="col" className="px-3 py-2">Entra department</th>
            <th scope="col" className="px-3 py-2">Job title</th>
            <th scope="col" className="px-3 py-2">Account</th>
            <th scope="col" className="px-3 py-2">{action ? '' : 'Why'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.PersonId} className="border-t border-border odd:bg-white even:bg-bg/60">
              <td className="px-3 py-2">
                <Link to={`/people/${m.PersonId}`} className="font-semibold text-navy hover:underline">{m.DisplayName}</Link>
                <div className="text-xs text-darkGrey/70">{m.PrimaryEmail}</div>
              </td>
              {/* Quoted, whitespace kept: a trailing space or blank is exactly why someone is not caught. */}
              <td className="px-3 py-2 font-mono text-xs">
                <span className="whitespace-pre">{m.EntraDepartment === null ? '(blank)' : `"${m.EntraDepartment}"`}</span>
                {m.Reason === 'DepartmentMismatch' && m.DepartmentCode && (
                  <div className="font-sans text-darkGrey/70">Oscar: {m.DepartmentCode}</div>
                )}
              </td>
              <td className="px-3 py-2 text-xs">{m.JobTitle ?? '—'}</td>
              <td className="px-3 py-2">
                <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', m.Active ? 'bg-green/15 text-green' : 'bg-red/10 text-red')}>
                  {m.Active ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-darkGrey/80">
                {action ? action(m) : REASON[m.Reason]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DynamicRuleBuilderPage() {
  const [peopleQuery, setPeopleQuery] = useState('');
  const [peopleDebounced, setPeopleDebounced] = useState('');
  const [names, setNames] = useState<Record<string, Named>>({});
  const [submitted, setSubmitted] = useState<DynamicRuleBuildRequest | null>(null);

  const { register, handleSubmit, setValue, watch, getValues, formState: { errors } } = useForm<DynamicRuleBuildRequest>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });
  const values = watch();

  useEffect(() => {
    const t = setTimeout(() => setPeopleDebounced(peopleQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [peopleQuery]);

  const departmentsQuery = useQuery({
    queryKey: ['departments', { includeDeleted: false }],
    queryFn: ({ signal }) => listDepartments(false, signal),
  });
  const titlesQuery = useQuery({
    queryKey: TITLES_KEY,
    queryFn: ({ signal }) => listPositionTitles(undefined, signal),
  });
  const peopleSearch = useQuery({
    queryKey: ['dynamic-rule-builder-people', peopleDebounced],
    queryFn: ({ signal }) => listPeople({ search: peopleDebounced, pageSize: 10 }, signal),
    enabled: peopleDebounced.length >= MIN_SEARCH,
    retry: false,
  });
  const build = useQuery({
    queryKey: ['dynamic-rule-build', submitted],
    queryFn: ({ signal }) => buildDynamicRule(submitted!, signal),
    enabled: submitted !== null,
    staleTime: 0,
    retry: false,
  });

  const departments = useMemo(() => departmentsQuery.data ?? [], [departmentsQuery.data]);
  const titles = useMemo(() => titlesQuery.data ?? [], [titlesQuery.data]);
  const describe = (code: string) => departments.find((d) => d.DepartmentCode === code)?.DepartmentDescription;

  const set = (field: 'DepartmentCodes' | 'PositionTitles' | 'IncludeObjectIds' | 'ExcludeObjectIds', next: string[]) =>
    setValue(field, next, { shouldValidate: submitted !== null || errors.DepartmentCodes !== undefined });

  const name = (id: string, who: Named) => setNames((prev) => ({ ...prev, [id]: who }));
  // Including someone takes them off the exclude list and vice versa: one person, one instruction.
  const includePerson = (id: string, who: Named) => {
    name(id, who);
    set('ExcludeObjectIds', getValues('ExcludeObjectIds').filter((x) => x !== id));
    set('IncludeObjectIds', addUnique(getValues('IncludeObjectIds'), [id]));
  };
  const excludePerson = (id: string, who: Named) => {
    name(id, who);
    set('IncludeObjectIds', getValues('IncludeObjectIds').filter((x) => x !== id));
    set('ExcludeObjectIds', addUnique(getValues('ExcludeObjectIds'), [id]));
  };

  const onCreate = handleSubmit((v) => setSubmitted({ ...v }));
  const stale = submitted !== null && !sameRequest(submitted, values);
  const result = build.data;
  const errorMessage = describeSearchError(build.error);
  const toggleDepartment = (code: string) => set('DepartmentCodes', values.DepartmentCodes.includes(code)
    ? values.DepartmentCodes.filter((c) => c !== code)
    : addUnique(values.DepartmentCodes, [code]));

  return (
    <section>
      <header className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-navy">Dynamic rule builder</h1>
        <p className="mt-1 max-w-3xl text-sm text-darkGrey/80">
          Build an Entra dynamic group rule from department codes and position titles, see who it would include, then copy
          it into the group yourself. Nothing is changed in Entra. To check where a title or department already appears in a
          rule, use <Link to="/dynamic-rules" className="font-semibold text-navy underline">Dynamic rule search</Link>.
        </p>
      </header>

      <form onSubmit={onCreate} noValidate className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          {/* ── 1. Departments ───────────────────────────────────────────── */}
          <div className="rounded border border-border bg-white p-4 shadow-sm">
            <StepHeading n={1}>Departments</StepHeading>
            <p className="mb-3 text-xs text-darkGrey/80">
              Tick a department to match its code in Entra&rsquo;s <span className="font-mono">department</span> field.
              Expand one to add a single job title as well.
            </p>
            <DepartmentPicker
              departments={departments}
              titles={titles}
              loading={departmentsQuery.isLoading || titlesQuery.isLoading}
              failed={departmentsQuery.isError || titlesQuery.isError}
              pickedDepartments={values.DepartmentCodes}
              pickedTitles={values.PositionTitles}
              onToggleDepartment={toggleDepartment}
              onAddDepartments={(codes) => set('DepartmentCodes', addUnique(values.DepartmentCodes, codes))}
              onAddTitle={(t) => set('PositionTitles', addUnique(values.PositionTitles, [t]))}
            />

            <div className="mt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">In the rule</h3>
              {values.DepartmentCodes.length === 0 && values.PositionTitles.length === 0 ? (
                <p className="mt-1 text-sm text-darkGrey/70">Nothing yet. Tick departments above.</p>
              ) : (
                <ul className="mt-1 flex flex-wrap gap-2" aria-label="Departments and titles in the rule">
                  {values.DepartmentCodes.map((c) => (
                    <Chip key={`d:${c}`} label={c} onRemove={() => toggleDepartment(c)}>
                      <span className="font-mono font-semibold">{c}</span>
                      {describe(c) && <> · {describe(c)}</>}
                    </Chip>
                  ))}
                  {values.PositionTitles.map((t) => (
                    <Chip key={`t:${t}`} label={t} onRemove={() => set('PositionTitles', values.PositionTitles.filter((x) => x !== t))}>
                      <span className="text-darkGrey/70">Title:</span> {t}
                    </Chip>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* ── 2. Account state ──────────────────────────────────────────── */}
          <fieldset className="rounded border border-border bg-white p-4 shadow-sm">
            <legend className="sr-only">Account state</legend>
            <StepHeading n={2}>Account state</StepHeading>
            <div className="space-y-2">
              {STATES.map((s) => (
                <label key={s.value} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input type="radio" value={s.value} className="mt-1" {...register('AccountState')} />
                  <span>
                    <span className="font-semibold text-darkGrey">{s.label}</span>
                    <span className="block font-mono text-xs text-darkGrey/70">{s.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-darkGrey/70">Applies to named people too: a disabled person named in an &ldquo;enabled only&rdquo; rule is not a member.</p>
          </fieldset>

          {/* ── 3. Named people ───────────────────────────────────────────── */}
          <div className="rounded border border-border bg-white p-4 shadow-sm">
            <StepHeading n={3}>Specific people (optional)</StepHeading>
            <label htmlFor="rule-people-q" className="block text-sm font-semibold text-navy">Find a person to include or leave out</label>
            <input
              id="rule-people-q"
              type="search"
              autoComplete="off"
              placeholder="Name, email or employee ID"
              value={peopleQuery}
              onChange={(e) => setPeopleQuery(e.target.value)}
              className="mt-1 w-full rounded border border-border bg-white px-3 py-2 text-sm"
            />
            {peopleDebounced.length >= MIN_SEARCH && (
              <div className="mt-2" aria-live="polite">
                {peopleSearch.isFetching && <p className="text-sm text-darkGrey/70">Searching…</p>}
                {peopleSearch.isError && <p className="text-sm text-red" role="alert">People could not be searched.</p>}
                {peopleSearch.data && peopleSearch.data.Items.length === 0 && <p className="text-sm text-darkGrey/70">No one matches.</p>}
                {peopleSearch.data && peopleSearch.data.Items.length > 0 && (
                  <ul className="divide-y divide-border rounded border border-border">
                    {peopleSearch.data.Items.map((p: Person) => {
                      const id = p.EntraObjectId;
                      const who = { name: p.DisplayName, email: p.PrimaryEmail };
                      return (
                        <li key={p.PersonId} className="flex flex-wrap items-center gap-2 px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-semibold text-darkGrey">{p.DisplayName}</span>
                            <div className="text-xs text-darkGrey/70">{p.PrimaryEmail}{p.JobTitle && <> · {p.JobTitle}</>}</div>
                          </div>
                          {id ? (
                            <>
                              <button type="button" onClick={() => includePerson(id, who)} disabled={values.IncludeObjectIds.includes(id)}
                                aria-label={`Include ${p.DisplayName}`}
                                className="inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy hover:bg-navy/5 disabled:opacity-50">
                                <UserPlus className="h-3.5 w-3.5" aria-hidden /> Include
                              </button>
                              <button type="button" onClick={() => excludePerson(id, who)} disabled={values.ExcludeObjectIds.includes(id)}
                                aria-label={`Leave out ${p.DisplayName}`}
                                className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-semibold text-darkGrey hover:bg-bg disabled:opacity-50">
                                <UserMinus className="h-3.5 w-3.5" aria-hidden /> Leave out
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-darkGrey/70">Not linked to an Entra account</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {(values.IncludeObjectIds.length > 0 || values.ExcludeObjectIds.length > 0) && (
              <div className="mt-3 space-y-2">
                {values.IncludeObjectIds.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">Always include</h3>
                    <ul className="mt-1 flex flex-wrap gap-2" aria-label="People always included">
                      {values.IncludeObjectIds.map((id) => (
                        <Chip key={id} label={names[id]?.name ?? id} onRemove={() => set('IncludeObjectIds', values.IncludeObjectIds.filter((x) => x !== id))}>
                          {names[id]?.name ?? id}
                        </Chip>
                      ))}
                    </ul>
                  </div>
                )}
                {values.ExcludeObjectIds.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">Always leave out</h3>
                    <ul className="mt-1 flex flex-wrap gap-2" aria-label="People always left out">
                      {values.ExcludeObjectIds.map((id) => (
                        <Chip key={id} label={names[id]?.name ?? id} onRemove={() => set('ExcludeObjectIds', values.ExcludeObjectIds.filter((x) => x !== id))}>
                          {names[id]?.name ?? id}
                        </Chip>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-darkGrey/70">People are matched by their Entra object ID, so the rule still works if their name or email changes.</p>
              </div>
            )}
          </div>

          {errors.DepartmentCodes && <p className="text-sm text-red" role="alert">{errors.DepartmentCodes.message}</p>}
          <button type="submit" disabled={build.isFetching}
            className="inline-flex items-center gap-2 rounded bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-60">
            <Wand2 className="h-4 w-4" aria-hidden /> {submitted ? 'Create rule again' : 'Create rule'}
          </button>
        </div>

        {/* ── Result ─────────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {!submitted && (
            <div className="rounded border border-dashed border-border bg-white p-6 text-sm text-darkGrey/70">
              Pick departments, titles or people, then press <strong>Create rule</strong>. The rule and the people it would include appear here.
            </div>
          )}
          {build.isFetching && <p className="text-sm text-darkGrey/70" role="status">Building the rule…</p>}
          {errorMessage && !build.isFetching && <p className="text-sm text-red" role="alert">{errorMessage}</p>}

          {result && !build.isFetching && (
            <>
              {stale && (
                <div className="flex flex-wrap items-center gap-3 rounded border border-orange/40 bg-orange/10 p-3 text-sm text-darkGrey" role="status">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-orange" aria-hidden />
                  <span className="flex-1">Your picks have changed since this rule was made. Create it again before you copy it.</span>
                  <button type="submit" className="rounded bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy/90">Create rule again</button>
                </div>
              )}

              {result.Warnings.length > 0 && (
                <ul className="space-y-1 rounded border border-orange/40 bg-orange/5 p-3 text-sm text-darkGrey" aria-label="Warnings">
                  {result.Warnings.map((w) => (
                    <li key={w} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />{w}</li>
                  ))}
                </ul>
              )}

              {result.Rule && (
                <div className={cn('rounded border border-border bg-white p-4 shadow-sm', stale && 'opacity-60')}>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="font-heading text-lg font-bold text-navy">Membership rule</h2>
                    {!stale && <CopyButton value={result.Rule} label="Copy rule" />}
                  </div>
                  <pre aria-label="Membership rule" className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-bg/60 p-3 font-mono text-xs text-darkGrey">{result.Rule}</pre>
                  <div className="mt-2">
                    <div className="h-1.5 w-full overflow-hidden rounded bg-border">
                      <div className={cn('h-full', result.RuleLength > result.MaxRuleLength ? 'bg-red' : 'bg-navy')}
                        style={{ width: `${Math.min(100, (result.RuleLength / result.MaxRuleLength) * 100)}%` }} />
                    </div>
                    <p className={cn('mt-1 text-xs', result.RuleLength > result.MaxRuleLength ? 'font-semibold text-red' : 'text-darkGrey/70')}>
                      {fmt(result.RuleLength)} of {fmt(result.MaxRuleLength)} characters · {result.Departments.length} {result.Departments.length === 1 ? 'department' : 'departments'}
                      {result.Titles.length > 0 && <> · {result.Titles.length} {result.Titles.length === 1 ? 'job title' : 'job titles'}</>}
                    </p>
                  </div>

                  <details className="mt-3 text-sm text-darkGrey">
                    <summary className="cursor-pointer font-semibold text-navy">How to use it in Entra</summary>
                    <ol className="mt-2 list-decimal space-y-1 pl-5">
                      <li>In the Entra admin centre, open <strong>Groups</strong> and create a group (or open the one to change). Set <strong>Membership type</strong> to <strong>Dynamic User</strong>.</li>
                      <li>Under <strong>Dynamic user members</strong>, choose <strong>Add dynamic query</strong>, then <strong>Edit</strong> on the rule syntax box.</li>
                      <li>Paste the rule and choose <strong>OK</strong>.</li>
                      <li>Before saving, use the <strong>Validate Rules</strong> tab to check two or three people from the list below.</li>
                      <li>Save. Entra can take a while to work out the members of a large group.</li>
                    </ol>
                  </details>

                  <p className="mt-3 flex gap-2 text-xs text-darkGrey/80">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-navy" aria-hidden />
                    The rule reads each person&rsquo;s department in Entra, so it follows them when HR moves them. It only works for people whose Entra department is filled in; anyone Oscar thinks belongs but Entra does not is listed below.
                  </p>
                </div>
              )}

              {result.NotCaught.length > 0 && (
                <div className="rounded border border-orange/40 bg-white p-4 shadow-sm">
                  <h2 className="font-heading text-lg font-bold text-navy">In these departments but not caught ({fmt(result.NotCaught.length)})</h2>
                  <p className="mb-2 text-xs text-darkGrey/80">
                    Oscar puts these people in a department you picked (from their job title), but their department in Entra is blank or
                    different, so the rule misses them. Set the department on their account (on-premises AD for synced accounts), or name them in the rule.
                  </p>
                  <MemberTable
                    rows={result.NotCaught}
                    caption="People in a picked department that the rule does not catch"
                    action={(m) => m.EntraObjectId && (
                      <button type="button"
                        onClick={() => {
                          includePerson(m.EntraObjectId!, { name: m.DisplayName, email: m.PrimaryEmail });
                          setSubmitted({ ...getValues() });
                        }}
                        aria-label={`Include ${m.DisplayName} and create the rule again`}
                        className="inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy hover:bg-navy/5">
                        <UserPlus className="h-3.5 w-3.5" aria-hidden /> Include
                      </button>
                    )}
                  />
                </div>
              )}

              {result.Rule && (
                <div className="rounded border border-border bg-white p-4 shadow-sm">
                  <h2 className="font-heading text-lg font-bold text-navy">Who it would include ({fmt(result.Members.length)})</h2>
                  <p className="mb-2 text-xs text-darkGrey/80">
                    From Oscar&rsquo;s people register, as last synced from Entra. Only people linked to an Entra account are shown.
                    Entra is the final word: an account Oscar does not hold, such as a service account with one of these departments, would also be included.
                    {result.UnlinkedMatches > 0 && <> {fmt(result.UnlinkedMatches)} more {result.UnlinkedMatches === 1 ? 'person matches' : 'people match'} in Oscar but {result.UnlinkedMatches === 1 ? 'has' : 'have'} no Entra link, so {result.UnlinkedMatches === 1 ? 'is' : 'are'} not shown.</>}
                  </p>
                  {result.Members.length === 0
                    ? <p className="text-sm text-darkGrey">No one in Oscar matches this rule.</p>
                    : <MemberTable rows={result.Members} caption="People the rule would include" />}
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </section>
  );
}
